<script lang="ts">
    import {onMount} from "svelte";
    import Button from "$lib/elements/Button.svelte";
    import BonusOffers from "$lib/components/ModalContents/DepositWithdraw/Deposit/BonusOffers.svelte";
    import type {IPaymentOption} from "$lib/interfaces/IPaymentOption";
    import {paymentService} from "$lib/services/payment.service";
    import {environment} from '$lib/env';
    import {getCookie} from "$lib/utils/cookies";
    import CustomDropdown from "$lib/elements/CurrencyDropdown.svelte";

    interface Props {
        selectedPaymentOption: IPaymentOption;
    }

    let { selectedPaymentOption }: Props = $props();

    // Define the currency type based on the IPaymentOption interface
    interface CurrencyType {
        currencyName: string;
        minDepositAmount: number;
        maxDepositAmount: number;
    }

    let payment: any = $state({
        amount: '',
        currency: '', // Will be set after user data is fetched
        locale: 'en',
        additionalFields: {}
    });

    // Initialize with null, will be set after fetching user data
    let selectedCurrency: CurrencyType | null = $state(null);
    let currencies = selectedPaymentOption.currencies;
    let loading = $state(false);
    let fetchingUserData = $state(true);
    let debugInfo = $state('');

    onMount(async () => {
        // Get user data to access activeCryptoCurrency
        try {
            const apiUrl = `${environment.apiUrl}/auth/user`;
            // Get the authentication token from cookie
            const token = getCookie("forza_token");

            if (!token) {
                initializeDefaultCurrency();
                return;
            }

            // Make the authenticated request with Bearer token
            const response = await fetch(apiUrl, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`API request failed with status ${response.status}`);
            }

            const result = await response.json();

            if (result.status === 200 && result.data) {
                // Get user's active currency
                const userCurrency = result.data.activeCryptoCurrency;

                if (userCurrency) {
                    // Find matching currency
                    const matchingCurrency = selectedPaymentOption.currencies.find(
                        currency => currency.currencyName === userCurrency
                    );

                    if (matchingCurrency) {
                        selectedCurrency = matchingCurrency;
                        payment.currency = matchingCurrency.currencyName;
                    } else {
                        initializeDefaultCurrency();
                    }
                } else {
                    initializeDefaultCurrency();
                }
            } else {
                initializeDefaultCurrency();
            }
        } catch (error) {
            debugInfo += `Error: ${error.message}\n`;
            initializeDefaultCurrency();
        } finally {
            fetchingUserData = false;
        }
    });

    // Fallback function if user data can't be fetched
    function initializeDefaultCurrency() {
        const eurCurrency = selectedPaymentOption.currencies.find(
            currency => currency.currencyName === 'EUR'
        );
        selectedCurrency = eurCurrency || selectedPaymentOption.currencies[0];
        payment.currency = selectedCurrency.currencyName;
        debugInfo += `Default currency selected: ${selectedCurrency.currencyName}\n`;
        fetchingUserData = false;
    }

    function initPayment() {
        if (!selectedCurrency || payment.amount === '' || payment.amount === '0') {
            return;
        }
        if (loading) {
            return;
        }
        if (selectedPaymentOption.fields) {
            selectedPaymentOption.fields.map((field) => {
                setValueToAdditionalFields(field);
            });
        }
        loading = true;
        paymentService.paymentInit(selectedPaymentOption.id, payment).then((res) => {
            if (res.data.status === 200) {
                if (res.data.data.action === 'REDIRECT') {
                    window.location.href = res.data.data.url;
                } else if (res.data.data.action === 'FORM_SUBMIT') {
                    let form = document.createElement('form');
                    form.method = 'POST';
                    form.action = res.data.data.url;
                    Object.keys(res.data.data.formData).forEach((key) => {
                        let input = document.createElement('input');
                        input.type = 'hidden';
                        input.name = key;
                        input.value = res.data.data.formData[key];
                        form.appendChild(input);
                    });
                    document.body.appendChild(form);
                    form.submit();
                }
            }
            console.log('init payment');
            loading = false;
        });
    }

    function setValueToAdditionalFields(field: any) {
        if (field === 'browserColorDepth') {
            payment.additionalFields[field.fieldName].value = window.screen.colorDepth;
        } else if (field === 'browserLanguage') {
            payment.additionalFields[field.fieldName].value = navigator.language;
        } else if (field === 'browserTimezone') {
            payment.additionalFields[field.fieldName].value = Intl.DateTimeFormat().resolvedOptions().timeZone;
        } else if (field === 'browserTimezoneOffset') {
            payment.additionalFields[field.fieldName].value = new Date().getTimezoneOffset();
        } else if (field === 'browserScreenHeight') {
            payment.additionalFields[field.fieldName].value = window.screen.height;
        } else if (field === 'browserScreenWidth') {
            payment.additionalFields[field.fieldName].value = window.screen.width;
        } else if (field === 'browserJavaScriptEnabled') {
            payment.additionalFields[field.fieldName].value = true;
        } else if (field === 'browserJavaEnabled') {
            payment.additionalFields[field.fieldName].value = false;
        } else if (field === 'browserAcceptHeader') {
            payment.additionalFields[field.fieldName].value = '';
        }
    }

    // Function to handle currency selection from dropdown
    function selectCurrency(event) {
        selectedCurrency = event.detail;
        payment.currency = event.detail.currencyName;
        console.log("Currency manually selected:", event.detail);
    }

    // Get currency symbol
    function getCurrencySymbol(currencyName: string) {
        switch(currencyName) {
            case 'EUR': return '€';
            case 'USD': return '$';
            case 'GBP': return '£';
            case 'CAD': return 'C$';
            default: return '';
        }
    }

    // Format currency for display in dropdown
    function formatCurrency(currency) {
        return `${getCurrencySymbol(currency.currencyName)} ${currency.currencyName}`;
    }

    // Function to get currency icon name
    function getCurrencyIconName(currencyName) {
        return currencyName.toLowerCase();
    }

    // Handle image error
    function handleImageError(event) {
        const imgElement = event.target;
        imgElement.src = '/currencies/default.png';
    }
</script>

{#if fetchingUserData}
    <div class="flex justify-center items-center p-4">
        <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-fire"></div>
    </div>
{:else if selectedCurrency}
    <div class="flex justify-between text-xs w-full my-2 px-2">
        <span>You can only use your own payment account!</span>
        <span>Commission 0%</span>
    </div>

    <!-- Currency Dropdown -->
    <div class="w-full mb-4">
        <label class="text-sm text-primary-lightBlue ml-2">Select Currency</label>
        <div class="flex justify-center mt-2">
            <div class="w-48">
                <CustomDropdown
                        selected={selectedCurrency}
                        selectedTitle="currencyName"
                        items={currencies}
                        hasIcon={true}
                        on:selected={selectCurrency}
                        id="currencyDropdown"
                        formatLabel={formatCurrency}
                />
            </div>
        </div>
    </div>

    <div class="w-full md:mt-2">
        <label class="text-sm text-primary-lightBlue ml-2" for="bankTransferAmount">
            Amount {#if selectedCurrency.minDepositAmount}
            ({getCurrencySymbol(selectedCurrency.currencyName)}{selectedCurrency.minDepositAmount} -
            {getCurrencySymbol(selectedCurrency.currencyName)}{selectedCurrency.maxDepositAmount})
        {/if}
        </label>
        <input
                id="bankTransferAmount"
                bind:value={payment.amount}
                min={selectedCurrency.minDepositAmount}
                max={selectedCurrency.maxDepositAmount}
                type="number"
                placeholder="Amount"
                class="w-full bg-primary-dark py-2 mt-2 px-5 rounded-full outline-0"
        >
    </div>

    <div class="grid grid-cols-2 md:grid-cols-4 w-full gap-2.5 my-4">
        <button on:click={() => payment.amount = '50'} class="col-span-1 bg-primary-dark p-2 border-2 border-primary-lightBlue rounded-full text-sm font-thin" >
            {getCurrencySymbol(selectedCurrency.currencyName)}50
        </button>
        <button on:click={() => payment.amount = '100'} class="col-span-1 bg-primary-dark p-2 border-2 border-primary-lightBlue rounded-full text-sm font-thin" >
            {getCurrencySymbol(selectedCurrency.currencyName)}100
        </button>
        <button on:click={() => payment.amount = '200'} class="col-span-1 bg-primary-dark p-2 border-2 border-primary-lightBlue rounded-full text-sm font-thin" >
            {getCurrencySymbol(selectedCurrency.currencyName)}200
        </button>
        <button on:click={() => payment.amount = '300'} class="col-span-1 bg-primary-dark p-2 border-2 border-primary-lightBlue rounded-full text-sm font-thin" >
            {getCurrencySymbol(selectedCurrency.currencyName)}300
        </button>
    </div>

    <Button
            on:click={() => initPayment()}
            disabled={Number(payment.amount) < selectedCurrency.minDepositAmount || Number(payment.amount) > (selectedCurrency.maxDepositAmount ?? 1000000)}
            loading={loading}
            clas="w-full shadow-xl mt-3"
    >
        Deposit
    </Button>

    <div class="w-full">
        <BonusOffers type="FIAT" />
    </div>
{/if}